import React, { useState, useEffect } from "react";
import { Container, Row, Col, Button, Modal, Form, Breadcrumb } from "react-bootstrap";
import { CopyToClipboard } from "react-copy-to-clipboard";
import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
  EmailShareButton,
  FacebookIcon,
  TwitterIcon,
  LinkedinIcon,
  EmailIcon,
} from "react-share";
import "./blog.css";
import { useNavigate, useParams } from "react-router-dom";
import { useAppContext } from "../../context/app-context";
import { useAcubeService } from "../../service/acube-api.hook";
import { format } from "date-fns";
import SkeletonScreen from "../../layouts/skeleton";

/* -------------------
   Assets
------------------- */
const like =
  "https://d1o8ny1kec0w01.cloudfront.net/acube-blogs/thumbs-up-unfilled.svg";
const likeFilled =
  "https://d1o8ny1kec0w01.cloudfront.net/acube-blogs/thumbs-up-filled.svg";
const share =
  "https://d1o8ny1kec0w01.cloudfront.net/acube-blogs/share-icon.svg";
const copyLink =
  "https://d1o8ny1kec0w01.cloudfront.net/acube-blogs/link.svg";
const copySuccess =
  "https://d1o8ny1kec0w01.cloudfront.net/acube-blogs/copied.svg"; // Success tick icon

const APP_DOMAIN = process.env.REACT_APP_DOMAIN;

const AllPosts = () => {
  const [showModal, setShowModal] = useState(false);
  const [currentBlog, setCurrentBlog] = useState(null);
  const [isCopied, setIsCopied] = useState(false);
  const [blogData, setBlogData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const { ipAddress } = useAppContext();
  const { type } = useParams();
  const { getProxy, postProxy } = useAcubeService();
  const navigate = useNavigate();

  /* -------------------
     Derived Values
  ------------------- */
  const shareURL = currentBlog ? `${APP_DOMAIN}/blogs/${currentBlog.slug}` : "";

  const socialMediaLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${shareURL}`,
    twitter: `https://twitter.com/intent/tweet?url=${shareURL}&text=${encodeURIComponent(
      "Check this out",
    )}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${shareURL}`,
    email: `mailto:?subject=${encodeURIComponent("Check out this blog")}&body=${encodeURIComponent(
      "Read this blog: " + shareURL,
    )}`,
  };

  /* -------------------
     API Calls
  ------------------- */
  const fetchBlogs = async () => {
    try {
      setIsLoading(true);
      const result = await getProxy(`blog/${type}/${ipAddress}`);
      setBlogData(result.data || []);
    } catch (error) {
      console.error("Error fetching blogs:", error);
      setBlogData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const addViewsCount = async (uuid) => {
    try {
      await postProxy("blog/views", { blogUUID: uuid });
    } catch (error) {
      console.error("Error adding view count:", error);
    }
  };

  const handleLikeClick = async (blog) => {
    try {
      const alreadyLiked = blog.likes && blog.likes.length > 0;

      await postProxy(`blog/like`, { blogUUID: blog.blog_uuid, ip: ipAddress });

      setBlogData((prev) => {
        const updated = { ...prev };
        ["recentBlogs", "mostViewedBlogs", "popularBlogs"].forEach((key) => {
          if (Array.isArray(updated[key])) {
            updated[key] = updated[key].map((b) => {
              if (b.blog_uuid === blog.blog_uuid) {
                return alreadyLiked
                  ? { ...b, likesAdded: Math.max(0, b.likesAdded - 1), likes: [] }
                  : { ...b, likesAdded: b.likesAdded + 1, likes: [{}] };
              }
              return b;
            });
          }
        });
        return updated;
      });
    } catch (err) {
      console.log("Error toggling like:", err);
    }
  };

  /* -------------------
     Handlers
  ------------------- */
  const handleShareClick = (blog) => {
    setCurrentBlog(blog);
    setShowModal(true);
    setIsCopied(false);
  };

  const handleCloseModal = () => setShowModal(false);

  const handleCopy = () => setIsCopied(true);

  const handleCategoryClick = (category) => {
    const formattedType = category.trim().replace(/\s+/g, "-").toLowerCase();
    navigate(`/blogs/category/${formattedType}`);
  };

  const handleBlogClick = (blog) => {
    addViewsCount(blog.blog_uuid);
    navigate(`/blogs/${blog.slug}`);
  };

  /* -------------------
     Effects
  ------------------- */
  useEffect(() => {
    if (ipAddress) fetchBlogs();
  }, [ipAddress]); // Runs whenever ipAddress is available
  useEffect(() => {
    setIsLoading(true);
  }, []);
  /* -------------------
     Helpers
  ------------------- */
  const formatDate = (dateString) => format(new Date(dateString), "d MMMM yyyy");

  /* -------------------
     Render
  ------------------- */
  return (
    <section className="all-posts">
      <Container fluid className="py-3">
        {/* ------------------- Breadcrumb ------------------- */}
        <Row>
          <Col xs={12}>
            <Breadcrumb className="small mb-2">
              <Breadcrumb.Item onClick={() => navigate("/blogs")} className="cursor-pointer">
                Blog Home
              </Breadcrumb.Item>
              <Breadcrumb.Item active>
                {type === "popular"
                  ? "Popular Posts"
                  : type === "most-viewed"
                    ? "Most Viewed"
                    : "What's New"}
              </Breadcrumb.Item>
            </Breadcrumb>
          </Col>
        </Row>
        <div className="d-flex justify-content-between align-items-center">
          <h1 className="ft-wt-600 mb-4">
            {type === "popular"
              ? "Popular Posts"
              : type === "most-viewed"
                ? "Most Viewed"
                : "What's New"}
          </h1>
        </div>

        <Row className="blog-cards">
          {isLoading ? (
            <SkeletonScreen count={6} />
          ) : blogData.length > 0 ? (
            blogData.map((blog) => (
              <Col xs={12} md={6} lg={4} key={blog.id} className="mb-4 blog-card cursor-pointer">
                <img
                  src={blog.template_image}
                  alt={blog.title}
                  className="blog-thumbnail cursor-pointer"
                  onClick={() => handleBlogClick(blog)}
                />

                <div className="blog-card-info">
                  <div className="blog-meta">
                    <span
                      className="author cursor-pointer"
                      onClick={() => handleCategoryClick(blog.service_category)}
                    >
                      {blog.service_category}
                    </span>
                    <span className="date">{formatDate(blog.created_at)}</span>
                  </div>

                  <h3
                    className="blog-card-title cursor-pointer"
                    onClick={() => handleBlogClick(blog)}
                  >
                    {blog.title}
                  </h3>

                  <p className="blog-content cursor-pointer" onClick={() => handleBlogClick(blog)}>
                    {blog.template_content}
                  </p>

                  <div className="blog-actions justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                      <img
                        src={blog?.likes?.length ? likeFilled : like}
                        alt="thumbs-up"
                        className="cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLikeClick(blog);
                        }}
                      />
                      <img
                        src={share}
                        alt="share"
                        style={{ width: "16px" }}
                        className="cursor-pointer"
                        onClick={() => handleShareClick(blog)}
                      />
                    </div>

                    <div className="d-flex align-items-center gap-2">
                      <h6 className="mb-0">{blog.likesAdded} likes</h6>
                      <h6 className="mb-0">{blog.views} views</h6>
                    </div>
                  </div>
                </div>
              </Col>
            ))
          ) : (
            <p>No blogs available.</p>
          )}
        </Row>
      </Container>

      {/* Share Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title className="ft-wt-700">Share This Blog</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3 text-center">
              <Form.Label className="copy-link-label">Share to</Form.Label>
              <div className="d-flex gap-2 justify-content-center">
                <FacebookShareButton url={socialMediaLinks.facebook}>
                  <FacebookIcon size={48} round />
                </FacebookShareButton>
                <TwitterShareButton url={socialMediaLinks.twitter}>
                  <TwitterIcon size={48} round />
                </TwitterShareButton>
                <LinkedinShareButton url={socialMediaLinks.linkedin}>
                  <LinkedinIcon size={48} round />
                </LinkedinShareButton>
                <EmailShareButton url={socialMediaLinks.email}>
                  <EmailIcon size={48} round />
                </EmailShareButton>
              </div>
            </Form.Group>

            <Form.Group className="mb-3 position-relative">
              <Form.Label className="copy-link-label">Or copy the link</Form.Label>
              <div className="d-flex align-items-center">
                <Form.Control type="text" value={shareURL} readOnly />
                <CopyToClipboard text={shareURL} onCopy={handleCopy}>
                  <Button className="ms-2 p-0 copy-btn" variant="link">
                    <img
                      src={isCopied ? copySuccess : copyLink}
                      alt={isCopied ? "copied" : "copy"}
                      className="cursor-pointer"
                    />
                  </Button>
                </CopyToClipboard>
              </div>
              {isCopied && <div className="link-success-msg">Link copied successfully!</div>}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="link"
            className="ft-wt-700 text-primary text-decoration-none"
            onClick={handleCloseModal}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
};

export default AllPosts;
